import { api } from './api.ts';
import { isIOS, isStandalone } from './display.ts';

/**
 * Web push, and the honest description of where it does not work.
 *
 * On iOS the Push API exists only for a web app added to the Home Screen — never in a Safari
 * tab — and the permission prompt is ignored unless it comes straight out of a click handler.
 * So the UI has to know the difference between "you said no" and "this browser cannot ask yet".
 */

export type PushState =
  | 'unsupported'      // no service worker or no Push API at all
  | 'needs-install'    // iOS, in a tab: installing to the Home Screen is the precondition
  | 'not-configured'   // the server has no VAPID keys
  | 'default'          // can be asked
  | 'granted'
  | 'denied';

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) {
    return isIOS() && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (isIOS() && !isStandalone()) return 'needs-install';
  try {
    const { enabled } = await api.pushKey();
    if (!enabled) return 'not-configured';
  } catch {
    return 'not-configured';
  }
  return Notification.permission as PushState;
}

/** VAPID keys travel as base64url; PushManager wants raw bytes. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/** MUST be called straight from a click handler: iOS silently ignores a deferred request. */
export async function enablePush(): Promise<PushState> {
  const state = await getPushState();
  if (state !== 'default' && state !== 'granted') return state;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission as PushState;

  const { publicKey } = await api.pushKey();
  if (!publicKey) return 'not-configured';

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    }));

  const json = subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.pushSubscribe({ endpoint: json.endpoint, keys: json.keys });
  return 'granted';
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await api.pushUnsubscribe(subscription.endpoint).catch(() => {});
  await subscription.unsubscribe();
}
